import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { KpiCard, Section, RiskBadge } from '../../components/ui'
import { StatusBanner } from '../../components/StatusBanner'
import { Timeline } from '../../components/Timeline'
import { NotesPanel } from '../../components/NotesPanel'
import type { AdminPatientDetail as AdminPatientDetailType } from '../../lib/types'

export default function AdminPatientDetail() {
  const { patientId } = useParams<{ patientId: string }>()
  const [patient, setPatient] = useState<AdminPatientDetailType | null>(null)

  async function load() {
    setPatient(await api(`/admin/patients/${patientId}`))
  }

  useEffect(() => { load() }, [patientId])

  if (!patient || !patientId) return <div className="page-loading">Loading patient…</div>

  return (
    <div>
      <Link to="/admin/patients" className="muted" style={{ fontSize: 13 }}>&larr; All patients</Link>
      <div className="page-head">
        <h1>{patient.name}</h1>
        <div className="ph-sub">
          {patient.email || patient.phone || '—'} ·{' '}
          {patient.organization_name ? (
            <Link to={`/admin/organizations/${patient.organization_id}`}>{patient.organization_name}</Link>
          ) : 'No organization yet'}
        </div>
      </div>
      <p className="legal-note" style={{ marginTop: -8, marginBottom: 16 }}>
        Operational profile only — no clinical notes, treatment plans, or intake content are shown here.
      </p>

      {/* --- Journey --- */}
      <Section title="Journey">
        <StatusBanner
          riskLevel="low"
          journeyLabel={patient.journey.label}
          components={[
            { label: 'Session count', value: `${patient.journey.session_count} / 4` },
            { label: 'Case status', value: patient.case_status || '—' },
            { label: 'Status', value: patient.status },
          ]}
        />
      </Section>

      <div className="section-block">
        <div className="section-head"><h2>Profile</h2></div>
        <div className="kpis">
          <KpiCard label="Status" value={patient.status} />
          <KpiCard label="Created" value={new Date(patient.created_at).toLocaleDateString()} />
        </div>
      </div>

      {/* --- Timeline --- */}
      <Section title="Timeline">
        <Timeline patientId={patientId} limit={25} />
        <Link to="/admin/event-logs" className="muted" style={{ fontSize: 13 }}>View full event log &rarr;</Link>
      </Section>

      {/* --- Tasks & Notes --- */}
      <NotesPanel attachedToType="patient" attachedToId={patientId} organizationId={patient.organization_id} />

      {/* --- Assigned provider --- */}
      <Section title="Assigned provider">
        {patient.assigned_provider ? (
          <div className="row-list">
            <Link to={`/admin/providers/${patient.assigned_provider.id}`} className="row" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span>{patient.assigned_provider.name}</span>
            </Link>
          </div>
        ) : (
          <div className="empty">No provider assigned yet.</div>
        )}
      </Section>

      {/* --- Continuity --- */}
      <Section title="Continuity" note={`${patient.continuity_alerts_open.length} open alert(s)`}>
        <div className="row-list">
          {patient.continuity_alerts_open.map((a) => (
            <div key={a.id} className="row">
              <span><RiskBadge band={a.risk_band} /> {a.reason}</span>
              <span className="muted">{a.recommended_action}</span>
            </div>
          ))}
          {patient.continuity_alerts_open.length === 0 && <div className="empty">No open continuity alerts.</div>}
        </div>
      </Section>
    </div>
  )
}
